import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, signal } from '@angular/core';
import { ButtonDirective } from '../button/button.directive';
import { AVAILABLE_LANGUAGES, getLanguageOption, LanguageOption } from '../../services/localization/languages';
import { LocalizationService } from '../../services/localization/localization.service';

const languages = Array.from(AVAILABLE_LANGUAGES).sort((a, b) => a.code.localeCompare(b.code));

@Component({
  selector: 'lib-language-selector',
  imports: [ButtonDirective],
  templateUrl: './language-selector.component.html',
  styleUrl: './language-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'relative inline-flex',
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'close()',
  },
})
export class LanguageSelectorComponent {
  private readonly localizationService = inject(LocalizationService);
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly languages = input<readonly LanguageOption[]>(languages);

  protected readonly isOpen = signal(false);
  protected readonly listboxId = 'language-selector-options';

  protected readonly currentLanguage = computed(() => getLanguageOption(this.localizationService.currentLang()));

  protected toggle(event: Event): void {
    event.stopPropagation();
    this.isOpen.update(open => !open);
  }

  protected close(): void {
    this.isOpen.set(false);
  }

  protected selectLanguage(code: string, event: Event): void {
    event.stopPropagation();
    void this.localizationService.setLanguage(code);
    this.close();
  }

  protected isSelected(code: string): boolean {
    return this.localizationService.currentLang() === code;
  }

  protected onDocumentClick(event: Event): void {
    if (!this.isOpen()) {
      return;
    }

    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }
}
